import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trash2, Edit2, Plus, Search, Check, X, User as UserIcon, Lock, Globe, Camera, LayoutGrid, CheckSquare, Square, Minus, GripVertical, RotateCcw, ShieldAlert, History, List, Grid3X3, Phone, Briefcase, ArrowUpDown, Building2 } from 'lucide-react';
import * as Icons from 'lucide-react';
import ConfirmModal from '../../components/ui/ConfirmModal';
import CustomSelect from '../../components/ui/CustomSelect';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

function extractError(e, t) {
    const detail = e?.response?.data?.detail;
    if (!detail) return e?.message || (t ? t('error_occurred', 'Xatolik yuz berdi') : 'Xatolik yuz berdi');
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(d => `${d.loc?.at(-1) ?? ''}: ${d.msg}`).join('; ');
    return JSON.stringify(detail);
}

const inputCls = "w-full px-4 py-2.5 rounded-xl bg-surface-2 border border-border text-primary text-sm focus:outline-none focus:border-brand transition-colors";
const labelCls = "block text-[11px] font-bold text-muted uppercase tracking-wider mb-1.5";

function Toggle({ label, checked, onChange }) {
    return (
        <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" checked={checked} onChange={onChange} />
                <div className="w-10 h-5 bg-border rounded-full peer peer-checked:bg-brand
          after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white
          after:rounded-full after:h-4 after:w-4 after:transition-all
          peer-checked:after:translate-x-5" />
            </label>
            <span className="text-sm font-medium text-secondary">{label}</span>
        </div>
    );
}

const EMPTY = {
    login: '', last_name_ru: '', first_name_ru: '', middle_name_ru: '',
    last_name_uz: '', first_name_uz: '', middle_name_uz: '',
    last_name_en: '', first_name_en: '', middle_name_en: '',
    position_ru: '', position_uz: '', position_en: '',
    direction_ru: '', direction_uz: '', direction_en: '',
    title_ru: '', title_uz: '', title_en: '',
    phone: '', password: '',
    role: 'user', session_timeout: 3600, subcategory_ids: [], gantt_project_ids: [], document_ids: [], organization_ids: [],
    subcategory_orders: {}, category_orders: {},
    can_create_users: false, can_delete_users: false,
    can_create_categories: false, can_edit_categories: false, can_delete_categories: false,
    can_create_subcategories: false, can_edit_subcategories: false, can_delete_subcategories: false,
    can_edit_subcategory_order: false, can_edit_category_order: false,
    can_manage_gantt: false, can_view_gantt: false,
    can_create_gantt: false, can_edit_gantt: false, can_delete_gantt: false,
    can_manage_organizations: false,
    is_head_type: false,
    can_approve_documents: false, can_create_documents: false, can_edit_documents: false,
    can_delete_documents: false, can_view_documents: false, can_export_documents: false,
    can_manage_doc_approvers: false, can_import_doc_files: false, can_unlock_documents: false,
    can_delete_any_comment: false, can_view_draft_documents: false,
    is_superadmin: false, show_photo: true, default_page: 'admin', created_at: null,
};

export default function UsersPage() {
    const { t, i18n } = useTranslation();
    const lang = i18n.language === 'en' ? 'en' : (i18n.language === 'uz' ? 'uz' : 'ru');
    const ln = (u, field) => u[`${field}_${lang}`] || u[`${field}_en`] || u[`${field}_ru`] || u[`${field}_uz`] || '';
    const pos = (u) => u[`position_${lang}`] || u[`position_en`] || u[`position_ru`] || u[`position_uz`] || '';
    const [users, setUsers] = useState([]);
    const [subcats, setSubcats] = useState([]);
    const [ganttProjects, setGanttProjects] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user: currentUser } = useAuth();
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [editId, setEditId] = useState(null);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [formError, setFormError] = useState('');
    const [photo, setPhoto] = useState(null);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('usersViewMode') || 'list');
    const [sortMode, setSortMode] = useState('newest');
    const [searchQuery, setSearchQuery] = useState('');
    function changeViewMode(mode) { setViewMode(mode); localStorage.setItem('usersViewMode', mode); }
    const [showPhoto, setShowPhoto] = useState(true);
    // Password re-confirmation before saving edits
    const [showConfirmPwd, setShowConfirmPwd] = useState(false);
    const [confirmPwd, setConfirmPwd] = useState('');
    const [confirmPwdError, setConfirmPwdError] = useState('');

    // Secure Delete State
    const [deletingUser, setDeletingUser] = useState(null);
    const [deleteCreds, setDeleteCreds] = useState({ login: '', password: '' });
    const [deleteError, setDeleteError] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);

    // Secure Restore State
    const [restoringUser, setRestoringUser] = useState(null);
    const [restoreCreds, setRestoreCreds] = useState({ login: '', password: '' });
    const [restoreError, setRestoreError] = useState('');
    const [isRestoringConfirm, setIsRestoringConfirm] = useState(false);

    useEffect(() => { loadAll(); }, []);

    async function loadAll() {
        setLoading(true);
        try {
            const [uRes, sRes, gRes, dRes, oRes] = await Promise.all([
                api.get('/admin/users').catch(e => { console.error(e); return { data: [] }; }),
                api.get('/admin/subcategories').catch(e => { console.error(e); return { data: [] }; }),
                api.get('/gantt/projects?admin=true').catch(e => { console.error(e); return { data: [] }; }),
                api.get('/docs?admin_mode=true').catch(e => { console.error(e); return { data: [] }; }),
                api.get('/organizations').catch(e => { console.error(e); return { data: [] }; })
            ]);
            setUsers(uRes.data || []);
            setSubcats(sRes.data || []);
            setGanttProjects(gRes.data || []);
            setDocuments(dRes.data || []);
            setOrganizations(oRes.data || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }

    // Restore form state from URL on page refresh
    useEffect(() => {
        if (loading || users.length === 0) return;
        const action = searchParams.get('action');
        const id = searchParams.get('id');
        if (action === 'create' && !showForm) {
            setEditId(null); setForm(EMPTY); setPhoto(null); setShowPhoto(true); setFormError(''); setShowForm(true);
        } else if (action === 'edit' && id && !showForm) {
            const u = users.find(x => String(x.id) === String(id));
            if (u) openEdit(u);
        }
    }, [loading, users.length]); // eslint-disable-line react-hooks/exhaustive-deps

    function openCreate() {
        setEditId(null);
        setForm({
            ...EMPTY,
            organization_ids: organizations.length > 0 ? [organizations[0].id] : [],
        });
        setPhoto(null); setShowPhoto(true); setFormError(''); setShowForm(true);
        setSearchParams({ action: 'create' });
        document.getElementById('admin-content')?.scrollTo({ top: 0, behavior: 'smooth' });
    }
    function openEdit(u) {
        setEditId(u.id);
        const orgIds = (u.organizations || []).map(o => typeof o === 'string' ? o : o.id).concat(u.organization_ids || []);
        const uniqueOrgIds = Array.from(new Set(orgIds));
        setForm({
            login: u.login, last_name_ru: u.last_name_ru, first_name_ru: u.first_name_ru,
            middle_name_ru: u.middle_name_ru || '', last_name_uz: u.last_name_uz || '',
            first_name_uz: u.first_name_uz || '', middle_name_uz: u.middle_name_uz || '',
            position_ru: u.position_ru || '', position_uz: u.position_uz || '',
            phone: u.phone || '', password: '',
            role: u.role, session_timeout: u.session_timeout,
            organization_ids: uniqueOrgIds,
            subcategory_ids: u.subcategories?.map(s => s.id) || [],
            gantt_project_ids: u.gantt_project_ids || [],
            document_ids: u.document_ids || [],
            subcategory_orders: u.subcategory_order_map || {},
            category_orders: u.category_order_map || {},
            can_create_users: !!u.can_create_users,
            can_delete_users: !!u.can_delete_users,
            can_create_categories: u.can_create_categories,
            can_edit_categories: u.can_edit_categories,
            can_delete_categories: u.can_delete_categories,
            can_create_subcategories: u.can_create_subcategories,
            can_edit_subcategories: u.can_edit_subcategories,
            can_delete_subcategories: u.can_delete_subcategories,
            can_edit_subcategory_order: u.can_edit_subcategory_order,
            can_edit_category_order: u.can_edit_category_order,
            can_manage_gantt: u.can_manage_gantt,
            can_view_gantt: u.can_view_gantt,
            can_manage_organizations: !!u.can_manage_organizations,
            is_head_type: !!u.is_head_type,
            can_approve_documents: !!u.can_approve_documents,
            can_create_documents: !!u.can_create_documents,
            can_edit_documents: !!u.can_edit_documents,
            can_delete_documents: !!u.can_delete_documents,
            can_view_documents: !!u.can_view_documents,
            can_export_documents: !!u.can_export_documents,
            can_manage_doc_approvers: !!u.can_manage_doc_approvers,
            can_import_doc_files: !!u.can_import_doc_files,
            can_unlock_documents: !!u.can_unlock_documents,
            can_delete_any_comment: !!u.can_delete_any_comment,
            can_view_draft_documents: !!u.can_view_draft_documents,
            is_superadmin: !!u.is_superadmin,
            photo: u.photo || null,
            default_page: u.default_page || 'admin',
            created_at: u.created_at || null,
        });
        setPhoto(null); setShowPhoto(u.show_photo !== false); setFormError(''); setShowForm(true);
        setSearchParams({ action: 'edit', id: String(u.id) });
        document.getElementById('admin-content')?.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function fv(key) {
        return { value: form[key], onChange: e => setForm(p => ({ ...p, [key]: e.target.value })) };
    }

    function toggleSubcat(id) {
        setForm(p => ({
            ...p,
            subcategory_ids: p.subcategory_ids.includes(id)
                ? p.subcategory_ids.filter(s => s !== id)
                : [...p.subcategory_ids, id],
        }));
    }

    function toggleGanttProject(id) {
        setForm(p => ({
            ...p,
            gantt_project_ids: p.gantt_project_ids.includes(id)
                ? p.gantt_project_ids.filter(g => g !== id)
                : [...p.gantt_project_ids, id],
        }));
    }

    function toggleDocument(id) {
        setForm(p => ({
            ...p,
            document_ids: p.document_ids.includes(id)
                ? p.document_ids.filter(d => d !== id)
                : [...p.document_ids, id],
        }));
    }

    function toggleOrganization(id) {
        setForm(p => ({
            ...p,
            organization_ids: (p.organization_ids || []).includes(id)
                ? p.organization_ids.filter(o => o !== id)
                : [...(p.organization_ids || []), id],
        }));
    }

    // Group and Sort subcategories by category
    const groupedSubcats = useMemo(() => {
        const map = new Map();
        // Deduplicate subcats by ID to prevent duplicate rendering
        const uniqueSubcats = [];
        const seenIds = new Set();
        subcats.forEach(s => {
            if (s && s.id && !seenIds.has(s.id)) {
                uniqueSubcats.push(s);
                seenIds.add(s.id);
            }
        });

        uniqueSubcats.forEach(s => {
            const key = s.category_id;
            if (!map.has(key)) {
                map.set(key, { 
                    id: s.category_id, 
                    name: s.category_name_ru || key, 
                    global_order: s.category_order || 0,
                    icon_url: s.category_icon_url || '',
                    icon_name: s.category_icon_name || '',
                    show_icon: s.category_show_icon ?? true,
                    items: [] 
                });
            }
            map.get(key).items.push(s);
        });

        const groups = [...map.values()];

        // Sort Categories: Custom User Order > Global Category Order
        groups.sort((a, b) => {
            const orderA = form.category_orders[a.id] ?? (a.global_order === 0 ? 999999 : a.global_order);
            const orderB = form.category_orders[b.id] ?? (b.global_order === 0 ? 999999 : b.global_order);
            return orderA - orderB;
        });

        // Sort Subcategories within each group: Custom User Order > Global Subcategory Order
        groups.forEach(group => {
            group.items.sort((a, b) => {
                const orderA = form.subcategory_orders[a.id] ?? (a.order === 0 ? 999999 : a.order);
                const orderB = form.subcategory_orders[b.id] ?? (b.order === 0 ? 999999 : b.order);
                return orderA - orderB;
            });
        });

        return groups;
    }, [subcats, form.category_orders, form.subcategory_orders]);

    function resetToGlobal() {
        setForm(p => ({
            ...p,
            subcategory_orders: {},
            category_orders: {},
        }));
    }

    function onDragEnd(result) {
        const { destination, source, type } = result;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        if (type === 'CATEGORY') {
            const newGroups = Array.from(groupedSubcats);
            const [moved] = newGroups.splice(source.index, 1);
            newGroups.splice(destination.index, 0, moved);

            const newOrders = {};
            newGroups.forEach((g, i) => {
                newOrders[g.id] = i + 1;
            });
            setForm(p => ({ ...p, category_orders: newOrders }));
        } else {
            // Subcategory within a group
            const group = groupedSubcats.find(g => String(g.id) === source.droppableId);
            if (!group) return;

            const newItems = Array.from(group.items);
            const [moved] = newItems.splice(source.index, 1);
            newItems.splice(destination.index, 0, moved);

            const newSubOrders = { ...form.subcategory_orders };
            newItems.forEach((item, i) => {
                newSubOrders[item.id] = i + 1;
            });
            setForm(p => ({ ...p, subcategory_orders: newSubOrders }));
        }
    }

    function toggleCategory(items) {
        const ids = items.map(s => s.id);
        const allSelected = ids.every(id => form.subcategory_ids.includes(id));
        setForm(p => ({
            ...p,
            subcategory_ids: allSelected
                ? p.subcategory_ids.filter(id => !ids.includes(id))
                : [...new Set([...p.subcategory_ids, ...ids])],
        }));
    }

    async function handleSave(e) {
        if (e) e.preventDefault();
        if (saving) return;

        // ── Client-side required-field check ─────────────────────────────────
        const missing = [];
        if (!form.login?.trim())           missing.push('Login');
        if (!editId && !form.password)     missing.push(t('users.password'));
        if (!form.last_name_ru?.trim())    missing.push(t('users.last_name_ru', 'Фамилия (RU)'));
        if (!form.first_name_ru?.trim())   missing.push(t('users.first_name_ru', 'Имя (RU)'));
        if (!form.last_name_uz?.trim())    missing.push(t('users.last_name_uz', 'Familiya (UZ)'));
        if (!form.first_name_uz?.trim())   missing.push(t('users.first_name_uz', 'Ismi (UZ)'));
        if (!form.position_ru?.trim())     missing.push(t('users.position_ru', 'Должность (RU)'));
        if (!form.position_uz?.trim())     missing.push(t('users.position_uz', 'Lavozim (UZ)'));

        if (missing.length > 0) {
            setFormError(`${t('users.req_fields')}${missing.join(', ')}`);
            return;
        }
        // ─────────────────────────────────────────────────────────────────────

        // ── When editing: require current user's password re-confirmation ─────
        if (editId) {
            setConfirmPwd('');
            setConfirmPwdError('');
            setShowConfirmPwd(true);
            return;
        }
        // ─────────────────────────────────────────────────────────────────────

        await executeSave();
    }

    // Runs after password confirmation (or directly on create)
    async function executeSave() {
        setSaving(true); setFormError('');
        try {
            if (editId) {
                // Construct clean payload for PUT (JSON)
                const payload = {};
                const allowedKeys = [
                    'login', 'phone', 'role', 'session_timeout',
                    'can_create_users', 'can_delete_users',
                    'can_create_categories', 'can_edit_categories', 'can_delete_categories',
                    'can_create_subcategories', 'can_edit_subcategories', 'can_delete_subcategories',
                    'can_edit_subcategory_order', 'can_edit_category_order',
                    'can_manage_gantt', 'can_view_gantt',
                    'can_manage_organizations',
                    'is_head_type',
                    'can_approve_documents', 'can_create_documents', 'can_edit_documents',
                    'can_delete_documents', 'can_view_documents', 'can_export_documents',
                    'can_manage_doc_approvers', 'can_import_doc_files', 'can_unlock_documents',
                    'can_delete_any_comment', 'can_view_draft_documents',
                    'last_name_ru', 'first_name_ru', 'middle_name_ru',
                    'last_name_uz', 'first_name_uz', 'middle_name_uz',
                    'last_name_en', 'first_name_en', 'middle_name_en',
                    'position_ru', 'position_uz', 'position_en',
                    'direction_ru', 'direction_uz', 'direction_en',
                    'title_ru', 'title_uz', 'title_en',
                    'password', 'default_page'
                ];

                allowedKeys.forEach(k => {
                    if (form[k] !== undefined) {
                        if (k === 'password') {
                            if (form[k] !== '') payload[k] = form[k];
                        } else if (typeof form[k] === 'string' && form[k].trim() === '') {
                            payload[k] = null;
                        } else {
                            payload[k] = form[k];
                        }
                    }
                });
                payload.show_photo = showPhoto;
                
                // Always include subcategory_ids if it's an array
                payload.subcategory_ids = Array.isArray(form.subcategory_ids) ? form.subcategory_ids : [];
                payload.gantt_project_ids = Array.isArray(form.gantt_project_ids) ? form.gantt_project_ids : [];
                payload.document_ids = Array.isArray(form.document_ids) ? form.document_ids : [];
                payload.organization_ids = Array.isArray(form.organization_ids) ? form.organization_ids : [];

                // Custom orders
                const cleanedSubOrders = {};
                payload.subcategory_ids.forEach(id => {
                    if (form.subcategory_orders[id]) {
                        cleanedSubOrders[id] = parseInt(form.subcategory_orders[id]);
                    }
                });
                payload.subcategory_orders = cleanedSubOrders;

                const cleanedCatOrders = {};
                const involvedCatIds = new Set(subcats.filter(s => payload.subcategory_ids.includes(s.id)).map(s => s.category_id));
                involvedCatIds.forEach(id => {
                    if (form.category_orders[id]) {
                        cleanedCatOrders[id] = parseInt(form.category_orders[id]);
                    }
                });
                payload.category_orders = cleanedCatOrders;

                if (!payload.password) delete payload.password;

                await api.put(`/admin/users/${editId}`, payload);
                // Upload new photo if selected
                if (photo) {
                    const fd = new FormData();
                    fd.append('photo', photo);
                    await api.put(`/admin/users/${editId}/photo`, fd);
                }
            } else {
                // POST: FormData
                const fd = new FormData();
                Object.entries(form).forEach(([k, v]) => {
                    if (k === 'is_superadmin') return; // Skip
                    if (k === 'subcategory_ids' || k === 'gantt_project_ids' || k === 'document_ids' || k === 'organization_ids') {
                        fd.append(k, (v || []).join(','));
                    } else if (k === 'subcategory_orders' || k === 'category_orders') {
                        fd.append(k, JSON.stringify(v || {}));
                    } else if (typeof v === 'boolean') {
                        fd.append(k, v ? 'true' : 'false');
                    } else if (v !== null && v !== undefined) {
                        fd.append(k, String(v));
                    }
                });
                if (photo) fd.append('photo', photo);
                await api.post('/admin/users', fd);
            }
            setShowForm(false); setSearchParams({}); loadAll();
        } catch (e) { 
            console.error("Save error:", e);
            setFormError(extractError(e)); 
        }
        finally { setSaving(false); }
    }

    // Verify current user's password then save
    async function confirmSave() {
        if (!confirmPwd.trim()) {
            setConfirmPwdError(t('users.enter_password', 'Parolni kiriting'));
            return;
        }
        setConfirmPwdError('');
        setSaving(true);
        try {
            // Verify password via login endpoint
            await api.post('/auth/verify-password', { password: confirmPwd });
            setShowConfirmPwd(false);
            await executeSave();
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message;
            setConfirmPwdError(msg.includes('verify') || e?.response?.status === 401
                ? t('users.wrong_password', "Parol noto'g'ri")
                : msg);
            setSaving(false);
        }
    }

    function handleDelete(u) {
        if (u.is_superadmin) {
            alert(t('users.delete_superadmin'));
            return;
        }
        setDeletingUser(u);
        setDeleteCreds({ login: '', password: '' });
        setDeleteError('');
    }

    async function confirmDeleteUser() {
        if (!deleteCreds.login.trim() || !deleteCreds.password.trim()) {
            setDeleteError(t('users.delete_modal_err_empty'));
            return;
        }
        setIsDeleting(true);
        setDeleteError('');
        try {
            await api.post('/auth/verify-password', { login: deleteCreds.login, password: deleteCreds.password });
            await api.delete(`/admin/users/${deletingUser.id}`);
            setDeletingUser(null);
            loadAll();
        } catch (e) {
            const msg = extractError(e);
            setDeleteError(e?.response?.status === 401 ? t('users.delete_modal_err_wrong') : msg);
        } finally {
            setIsDeleting(false);
        }
    }

    function handleRestore(u) {
        if (!currentUser?.is_superadmin) return;
        setRestoringUser(u);
        setRestoreCreds({ login: '', password: '' });
        setRestoreError('');
    }

    async function confirmRestoreUser() {
        if (!restoreCreds.login.trim() || !restoreCreds.password.trim()) {
            setRestoreError(t('users.restore_modal_err_empty', 'Login va parolni kiriting'));
            return;
        }
        setIsRestoringConfirm(true);
        setRestoreError('');
        try {
            await api.post('/auth/verify-password', { login: restoreCreds.login, password: restoreCreds.password });
            setIsRestoring(restoringUser.id);
            await api.put(`/admin/users/${restoringUser.id}/restore`);
            setRestoringUser(null);
            loadAll();
        } catch (e) {
            const msg = extractError(e);
            setRestoreError(e?.response?.status === 401 ? t('users.restore_modal_err_wrong', 'Login yoki parol noto\'g\'ri') : msg);
        } finally {
            setIsRestoringConfirm(false);
            setIsRestoring(false);
        }
    }

    const avatarBg = ['bg-brand', 'bg-indigo-500', 'bg-purple-500', 'bg-teal-500', 'bg-orange-500'];

    function renderPermissionSwitch(label, key, { coveredBy } = {}) {
        const isSuper = form.is_superadmin;
        const isSelf = form.id === currentUser?.id;
        const canGrant = !isSuper && (!isSelf || currentUser?.is_superadmin) && (currentUser?.is_superadmin || currentUser?.[key]);
        // Is this permission implicitly granted by a parent permission (e.g. can_manage_gantt)?
        const isCovered = coveredBy ? form[coveredBy] : false;
        const isChecked = isSuper || isCovered || form[key];

        return (
            <label className={`flex items-center gap-3 ${isCovered ? 'opacity-60 cursor-not-allowed' : !canGrant ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <span className={`flex-shrink-0 transition-colors ${isChecked ? (isCovered ? 'text-cyan-400' : 'text-brand') : 'text-muted'}`}>
                    {isChecked ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                </span>
                <span className="text-sm font-medium text-secondary">
                    {label}
                    {isCovered && <span className="ml-1.5 text-[10px] text-cyan-500 font-semibold">({t('users.full_access', "to'liq ruxsat")})</span>}
                </span>
                <input
                    type="checkbox"
                    className="hidden"
                    checked={isChecked}
                    disabled={!canGrant || isCovered}
                    onChange={e => !isSuper && !isCovered && setForm(p => ({ ...p, [key]: e.target.checked }))}
                />
            </label>
        );
    }

    const filteredUsers = useMemo(() => {
        // Asosiy ro'yxatda FAQAT aktiv userlar (is_active=true)
        // O'chirilgan userlar quyidagi alohida bo'limda ko'rsatiladi (faqat superadmin)
        let result = users.filter(u => u.is_active !== false);
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(u => {
                const fullNameRu = `${u.last_name_ru || ''} ${u.first_name_ru || ''} ${u.middle_name_ru || ''}`.toLowerCase();
                const fullNameUz = `${u.last_name_uz || ''} ${u.first_name_uz || ''} ${u.middle_name_uz || ''}`.toLowerCase();
                return fullNameRu.includes(q) ||
                    fullNameUz.includes(q) ||
                    (u.login || '').toLowerCase().includes(q) ||
                    (u.position_ru || '').toLowerCase().includes(q) ||
                    (u.position_uz || '').toLowerCase().includes(q);
            });
        }

        
        return [...result].sort((a, b) => {
            if (sortMode === 'newest') {
                const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
                const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
                return dateB - dateA;
            } else {
                const nameA = `${a.last_name_ru || ''} ${a.first_name_ru || ''}`.trim();
                const nameB = `${b.last_name_ru || ''} ${b.first_name_ru || ''}`.trim();
                return nameA.localeCompare(nameB);
            }
        });
    }, [users, searchQuery, sortMode]);

    return (
        <>
        <div className="space-y-6">
            {/* ── Header ── */}
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">{t('users.title')}</h2>
                <div className="flex items-center gap-3">
                    {/* View toggle */}
                    <div className="flex items-center bg-surface-2 border border-border rounded-xl p-0.5">
                        <button
                            onClick={() => changeViewMode('list')}
                            className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-brand text-inverse shadow-sm' : 'text-muted hover:text-primary'}`}
                            title={t('users.list_view') || 'Ro\'yxat'}
                        >
                            <List className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => changeViewMode('card')}
                            className={`p-2 rounded-lg transition-all ${viewMode === 'card' ? 'bg-brand text-inverse shadow-sm' : 'text-muted hover:text-primary'}`}
                            title={t('users.card_view') || 'Kartochka'}
                        >
                            <Grid3X3 className="w-4 h-4" />
                        </button>
                    </div>

                    {(currentUser?.is_superadmin || currentUser?.can_create_users) && (
                        <button
                            onClick={showForm ? () => { setShowForm(false); setSearchParams({}); } : openCreate}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-colors
                    ${showForm ? 'bg-surface-2 border border-border text-secondary' : 'bg-brand text-inverse hover:bg-brand-hover'}`}
                        >
                            {showForm ? <><X className="w-4 h-4" /> {t('users.cancel')}</> : <><Plus className="w-4 h-4" /> {t('users.add')}</>}
                        </button>
                    )}
                </div>
            </div>

            {/* ── Search ── */}
            {!showForm && (
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder={t('search')}
                        className="w-full pl-11 pr-4 py-2.5 rounded-xl bg-surface border border-border text-primary text-sm focus:outline-none focus:border-brand transition-colors"
                    />
                </div>
            )}

            {/* ── Form ── */}
            {showForm && (
                <div className="bg-surface border border-border rounded-2xl p-6 shadow-sm">
                    <h3 className="font-bold text-base mb-5">{editId ? t('users.edit_title') : t('users.create_title')}</h3>
                    {formError && (
                        <div className="mb-5 p-4 rounded-xl bg-error/10 border border-error/20 text-error text-sm font-medium">
                            {formError}
                        </div>
                    )}
                    <form onSubmit={handleSave} noValidate className="space-y-5">
                        {/* Login + Role */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className={labelCls}>{t('users.login')} <span className="text-error">*</span></label>
                                <input required {...fv('login')} className={inputCls} placeholder={t('users.placeholder_login')} autoComplete="off" />
                            </div>
                            <div>
                                <label className={labelCls}>{t('users.password')} {editId ? t('users.password_hint') : <span className="text-error">*</span>}</label>
                                <input type="password" {...fv('password')} required={!editId} className={inputCls} placeholder={t('users.placeholder_password')} autoComplete="new-password" />
                            </div>
                             <div>
                                <label className={labelCls}>{t('users.role')}</label>
                                {form.is_superadmin ? (
                                    <div className={`${inputCls} flex items-center gap-2 cursor-not-allowed opacity-70`}>
                                        <Lock className="w-3.5 h-3.5 text-brand" />
                                        <span className="font-semibold text-brand">Super Admin</span>
                                    </div>
                                ) : (
                                    <CustomSelect 
                                        value={form.role}
                                        onChange={val => setForm(p => ({ ...p, role: val }))}
                                        options={[
                                            { value: 'user', label: 'User' },
                                            { value: 'admin', label: 'Admin' },
                                            { value: 'gantt_user', label: 'Gantt User' },
                                            { value: 'doc_user', label: 'Doc User' }
                                        ]}
                                    />
                                )}
                             </div>
                             
                             <div>
                                 <label className={labelCls}>{t('users.session_timeout')}</label>
                                 <CustomSelect 
                                     value={form.session_timeout}
                                     onChange={val => setForm(p => ({ ...p, session_timeout: parseInt(val, 10) }))}
                                     options={[
                                         { value: 1800, label: '30 daqiqa (0.5h)' },
                                         { value: 3600, label: '1 soat' },
                                         { value: 43200, label: '12 soat' },
                                         { value: 86400, label: '24 soat' },
                                         { value: 604800, label: '7 kun' }
                                     ]}
                                 />
                             </div>

                            <div>
                                <label className={labelCls}>{t('users.default_page')}</label>
                                <CustomSelect 
                                    value={form.default_page}
                                    onChange={val => setForm(p => ({ ...p, default_page: val }))}
                                    options={[
                                        { value: 'admin', label: t('users.default_page_admin') },
                                        { value: 'panel', label: t('users.default_page_panel') }
                                    ]}
                                />
                            </div>

                            {/* Created At (Read-only) */}
                            {editId && form.created_at && (
                                <div>
                                    <label className={labelCls}>{t('users.created_at', "Qo'shilgan vaqti")}</label>
                                    <div className={`${inputCls} bg-surface-2/50 !text-secondary cursor-not-allowed opacity-80 flex items-center h-[46px]`}>
                                        {(() => {
                                            const d = new Date(form.created_at);
                                            const dd = String(d.getDate()).padStart(2, '0');
                                            const mm = String(d.getMonth() + 1).padStart(2, '0');
                                            const yyyy = d.getFullYear();
                                            const hh = String(d.getHours()).padStart(2, '0');
                                            const min = String(d.getMinutes()).padStart(2, '0');
                                            return `${dd}-${mm}-${yyyy}, ${hh}:${min}`;
                                        })()}
                                    </div>
                                </div>
                            )}

                            {/* ── ADMIN PERMISSIONS ── */}
                            {form.role === 'admin' && (
                                <div className="md:col-span-4 mt-2 border border-border rounded-xl p-5 bg-surface-2/30">
                                    <p className={`${labelCls} text-brand mb-4 flex items-center gap-2`}>
                                        <ShieldAlert className="w-4 h-4" /> {t('users.permissions_title')}
                                    </p>

                                    {/* Users & Categories */}
                                    <div className="mb-4">
                                        <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-3 flex items-center gap-2">
                                            <Icons.Users className="w-3.5 h-3.5 text-blue-500" />
                                            {t('nav.users')} & {t('nav.categories')}
                                        </p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-6">
                                            {renderPermissionSwitch(t('users.perm_create_users'), 'can_create_users')}
                                            {renderPermissionSwitch(t('users.perm_delete_users'), 'can_delete_users')}
                                            {renderPermissionSwitch(t('users.perm_create_categories'), 'can_create_categories')}
                                            {renderPermissionSwitch(t('users.perm_edit_categories'), 'can_edit_categories')}
                                            {renderPermissionSwitch(t('users.perm_delete_categories'), 'can_delete_categories')}
                                            {renderPermissionSwitch(t('users.perm_edit_category_order'), 'can_edit_category_order')}
                                            {renderPermissionSwitch(t('users.perm_create_subcategories'), 'can_create_subcategories')}
                                            {renderPermissionSwitch(t('users.perm_edit_subcategories'), 'can_edit_subcategories')}
                                            {renderPermissionSwitch(t('users.perm_delete_subcategories'), 'can_delete_subcategories')}
                                            {renderPermissionSwitch(t('users.perm_edit_subcategory_order'), 'can_edit_subcategory_order')}
                                        </div>
                                    </div>

                                    {/* Organizations */}
                                    <div className="mt-4 pt-4 border-t border-border/50">
                                        <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-3 flex items-center gap-2">
                                            <Building2 className="w-3.5 h-3.5 text-emerald-500" />
                                            {t('nav.organizations', "Bo'limlar va Tashkilotlar")}
                                        </p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-6">
                                            {renderPermissionSwitch(t('users.perm_manage_organizations', "Tashkilotlarni boshqarish"), 'can_manage_organizations')}
                                        </div>
                                    </div>

                                    {/* Gantt */}
                                    <div className="mt-4 pt-4 border-t border-border/50">
                                        <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-3 flex items-center gap-2">
                                            <Icons.CalendarDays className="w-3.5 h-3.5 text-cyan-500" />
                                            Gantt Charts
                                        </p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-6">
                                            {renderPermissionSwitch(t('users.perm_manage_gantt'), 'can_manage_gantt')}
                                            {renderPermissionSwitch(t('users.perm_view_gantt'), 'can_view_gantt', { coveredBy: 'can_manage_gantt' })}
                                            {renderPermissionSwitch(t('users.perm_create_gantt'), 'can_create_gantt', { coveredBy: 'can_manage_gantt' })}
                                            {renderPermissionSwitch(t('users.perm_edit_gantt'), 'can_edit_gantt', { coveredBy: 'can_manage_gantt' })}
                                            {renderPermissionSwitch(t('users.perm_delete_gantt'), 'can_delete_gantt', { coveredBy: 'can_manage_gantt' })}
                                        </div>
                                    </div>

                                    {/* Documents */}
                                    <div className="mt-4 pt-4 border-t border-border/50">
                                        <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-3 flex items-center gap-2">
                                            <Icons.FileText className="w-3.5 h-3.5 text-brand" />
                                            {t('panel.documents', 'Hujjatlar')}
                                        </p>
                                        <div className="mb-3 p-3 rounded-xl bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 space-y-2">
                                            {renderPermissionSwitch(t('docs.head_approval_right', 'Head — hujjatni tasdiqlash huquqi'), 'is_head_type')}
                                            <p className="text-[10px] text-amber-600 dark:text-amber-400 ml-1">
                                                {t('docs.head_approval_desc', "Foydalanuvchiga hujjatni tasdiqlash huquqini beradi. Documentga qo'shilganda tasdiqlovchi sifatida tayinlanadi")}
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-6">
                                            {renderPermissionSwitch(t('users.perm_view_documents'), 'can_view_documents')}
                                            {renderPermissionSwitch(t('users.perm_create_documents'), 'can_create_documents')}
                                            {renderPermissionSwitch(t('users.perm_edit_documents'), 'can_edit_documents')}
                                            {renderPermissionSwitch(t('users.perm_delete_documents'), 'can_delete_documents')}
                                            {renderPermissionSwitch(t('users.perm_approve_documents'), 'can_approve_documents')}
                                            {renderPermissionSwitch(t('users.perm_export_documents'), 'can_export_documents')}
                                            {renderPermissionSwitch(t('users.perm_import_doc_files'), 'can_import_doc_files')}
                                            {renderPermissionSwitch(t('users.perm_manage_doc_approvers'), 'can_manage_doc_approvers')}
                                            {renderPermissionSwitch(t('users.perm_unlock_documents'), 'can_unlock_documents')}
                                            {renderPermissionSwitch(t('users.perm_delete_any_comment'), 'can_delete_any_comment')}
                                            {renderPermissionSwitch(t('users.perm_view_draft_documents'), 'can_view_draft_documents')}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── DOC_USER PERMISSIONS ── */}
                            {form.role === 'doc_user' && (
                                <div className="md:col-span-4 mt-2 border border-border rounded-xl p-5 bg-surface-2/30">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <Icons.FileText className="w-3.5 h-3.5 text-purple-500" />
                                        {t('panel.documents', 'Hujjatlar')} — Doc User
                                    </p>
                                    <div className="mb-3 p-3 rounded-xl bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 space-y-2">
                                        {renderPermissionSwitch(t('docs.head_approval_right', 'Head — hujjatni tasdiqlash huquqi'), 'is_head_type')}
                                        <p className="text-[10px] text-amber-600 dark:text-amber-400 ml-1">
                                            {t('docs.head_approval_desc', "Foydalanuvchiga hujjatni tasdiqlash huquqini beradi. Documentga qo'shilganda tasdiqlovchi sifatida tayinlanadi")}
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-6">
                                        {renderPermissionSwitch(t('users.perm_view_documents'), 'can_view_documents')}
                                        {renderPermissionSwitch(t('users.perm_create_documents'), 'can_create_documents')}
                                        {renderPermissionSwitch(t('users.perm_edit_documents'), 'can_edit_documents')}
                                        {renderPermissionSwitch(t('users.perm_delete_documents'), 'can_delete_documents')}
                                        {renderPermissionSwitch(t('users.perm_approve_documents'), 'can_approve_documents')}
                                        {renderPermissionSwitch(t('users.perm_export_documents'), 'can_export_documents')}
                                        {renderPermissionSwitch(t('users.perm_import_doc_files'), 'can_import_doc_files')}
                                        {renderPermissionSwitch(t('users.perm_manage_doc_approvers'), 'can_manage_doc_approvers')}
                                        {renderPermissionSwitch(t('users.perm_unlock_documents'), 'can_unlock_documents')}
                                        {renderPermissionSwitch(t('users.perm_delete_any_comment'), 'can_delete_any_comment')}
                                        {renderPermissionSwitch(t('users.perm_view_draft_documents'), 'can_view_draft_documents')}
                                    </div>
                                </div>
                            )}

                            {/* ── GANTT_USER PERMISSIONS ── */}
                            {form.role === 'gantt_user' && (
                                <div className="md:col-span-4 mt-2 border border-border rounded-xl p-5 bg-surface-2/30">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <Icons.CalendarDays className="w-3.5 h-3.5 text-cyan-500" />
                                        Gantt Charts — Gantt User
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-6 mb-4">
                                        {renderPermissionSwitch(t('users.perm_manage_gantt'), 'can_manage_gantt')}
                                        {renderPermissionSwitch(t('users.perm_view_gantt'), 'can_view_gantt', { coveredBy: 'can_manage_gantt' })}
                                        {renderPermissionSwitch(t('users.perm_create_gantt'), 'can_create_gantt', { coveredBy: 'can_manage_gantt' })}
                                        {renderPermissionSwitch(t('users.perm_edit_gantt'), 'can_edit_gantt', { coveredBy: 'can_manage_gantt' })}
                                        {renderPermissionSwitch(t('users.perm_delete_gantt'), 'can_delete_gantt', { coveredBy: 'can_manage_gantt' })}
                                    </div>
                                    {/* Head status */}
                                    <div className="pt-4 border-t border-border/50">
                                        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 space-y-2">
                                            {renderPermissionSwitch(t('docs.head_approval_right', 'Head — hujjatni tasdiqlash huquqi'), 'is_head_type')}
                                            <p className="text-[10px] text-amber-600 dark:text-amber-400 ml-1">
                                                {t('docs.head_approval_desc', "Foydalanuvchiga hujjatni tasdiqlash huquqini beradi. Documentga qo'shilganda tasdiqlovchi sifatida tayinlanadi")}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── USER (regular) — only head status ── */}
                            {form.role === 'user' && (
                                <div className="md:col-span-4 mt-2 border border-border rounded-xl p-5 bg-surface-2/30">
                                    <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 space-y-2">
                                        {renderPermissionSwitch(t('docs.head_approval_right', 'Head — hujjatni tasdiqlash huquqi'), 'is_head_type')}
                                        <p className="text-[10px] text-amber-600 dark:text-amber-400 ml-1">
                                            {t('docs.head_approval_desc', "Foydalanuvchiga hujjatni tasdiqlash huquqini beradi. Documentga qo'shilganda tasdiqlovchi sifatida tayinlanadi")}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* RU name fields */}
                        <div>
                            <p className={`${labelCls} text-brand mb-3`}>{t('users.full_name_ru')}</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div><label className={labelCls}>{t('users.last_name_ru', 'Фамилия (RU)')} <span className="text-error">*</span></label><input required {...fv('last_name_ru')} className={inputCls} placeholder={t('users.placeholder_last_name_ru', 'Иванов')} /></div>
                                <div><label className={labelCls}>{t('users.first_name_ru', 'Имя (RU)')} <span className="text-error">*</span></label><input required {...fv('first_name_ru')} className={inputCls} placeholder={t('users.placeholder_first_name_ru', 'Иван')} /></div>
                                <div><label className={labelCls}>{t('users.middle_name_ru', 'Отчество (RU)')}</label><input {...fv('middle_name_ru')} className={inputCls} placeholder={t('users.placeholder_middle_name_ru', 'Иванович')} /></div>
                            </div>
                        </div>

                        {/* UZ name fields */}
                        <div>
                            <p className={`${labelCls} text-brand mb-3`}>{t('users.full_name_uz')}</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div><label className={labelCls}>{t('users.last_name_uz', 'Familiya (UZ)')}</label><input {...fv('last_name_uz')} className={inputCls} placeholder={t('users.placeholder_last_name_uz', 'Ivanov')} /></div>
                                <div><label className={labelCls}>{t('users.first_name_uz', 'Ismi (UZ)')}</label><input {...fv('first_name_uz')} className={inputCls} placeholder={t('users.placeholder_first_name_uz', 'Ivan')} /></div>
                                <div><label className={labelCls}>{t('users.middle_name_uz', 'Otasining ismi (UZ)')} </label><input {...fv('middle_name_uz')} className={inputCls} placeholder={t('users.placeholder_middle_name_uz', 'Ivanovich')} /></div>
                            </div>
                        </div>

                        {/* EN name fields */}
                        <div>
                            <p className={`${labelCls} text-brand mb-3`}>{t('users.full_name_en', 'Full Name — English')}</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div><label className={labelCls}>{t('users.last_name_en', 'Last Name (EN)')}</label><input {...fv('last_name_en')} className={inputCls} placeholder={t('users.placeholder_last_name_en', 'Ivanov')} /></div>
                                <div><label className={labelCls}>{t('users.first_name_en', 'First Name (EN)')}</label><input {...fv('first_name_en')} className={inputCls} placeholder={t('users.placeholder_first_name_en', 'Ivan')} /></div>
                                <div><label className={labelCls}>{t('users.middle_name_en', 'Middle Name (EN)')}</label><input {...fv('middle_name_en')} className={inputCls} placeholder={t('users.placeholder_middle_name_en', 'Ivanovich')} /></div>
                            </div>
                        </div>

                        {/* Position + phone */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div><label className={labelCls}>{t('users.position_ru', 'Должность (RU)')} <span className="text-error">*</span></label><input required {...fv('position_ru')} className={inputCls} placeholder={t('users.placeholder_position_ru', 'Директор')} /></div>
                            <div><label className={labelCls}>{t('users.position_uz', 'Lavozim (UZ)')}</label><input {...fv('position_uz')} className={inputCls} placeholder={t('users.placeholder_position_uz', 'Direktor')} /></div>
                            <div><label className={labelCls}>{t('users.position_en', 'Position (EN)')}</label><input {...fv('position_en')} className={inputCls} placeholder={t('users.placeholder_position_en', 'Director')} /></div>
                            <div><label className={labelCls}>{t('users.phone')}</label><input {...fv('phone')} className={inputCls} placeholder={t('users.placeholder_phone')} /></div>
                        </div>

                        {/* Photo */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <label className="block text-[11px] font-bold text-muted uppercase tracking-wider">{t('users.photo')}</label>
                            </div>
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center gap-4">
                                    {(photo || form.photo) && (
                                        <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-surface-2 border border-border">
                                            <img 
                                                src={photo ? URL.createObjectURL(photo) : `${import.meta.env.VITE_API_URL || ''}${form.photo}`} 
                                                alt="" 
                                                className="w-full h-full object-cover" 
                                            />
                                        </div>
                                    )}
                                    
                                    <div className="relative flex-1">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={e => setPhoto(e.target.files[0])}
                                            className="hidden"
                                            id="photo-upload"
                                        />
                                        <label
                                            htmlFor="photo-upload"
                                            className={`flex items-center gap-2 w-full px-4 py-[11px] rounded-xl border border-dashed border-border cursor-pointer hover:border-brand transition-colors text-sm ${photo ? 'text-brand font-medium' : 'text-muted'}`}
                                        >
                                            <Camera className="w-4 h-4" />
                                            {photo ? photo.name : t('users.photo', 'Rasm tanlash')}
                                        </label>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end border-t border-border pt-3 mt-1">
                                    <Toggle
                                        label={t('users.show_photo', "Rasmni ko'rsatish")}
                                        checked={showPhoto}
                                        onChange={e => setShowPhoto(e.target.checked)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* ── Organization Access (Multi-Tenant) ── */}
                        <div className="bg-surface border border-border p-5 rounded-2xl md:col-span-2">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-xl bg-brand/10 text-brand">
                                        <Building2 className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <label className={labelCls}>{t('users.organization_access', "Tashkilotlarga ruxsat")}</label>
                                        <p className="text-xs text-muted">
                                            {t('users.organization_access_desc', "Foydalanuvchi faqat belgilangan tashkilotlar bo'limlari va ma'lumotlariga kira oladi")}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setForm(p => ({ ...p, organization_ids: organizations.map(o => o.id) }))}
                                        className="text-[11px] font-semibold text-brand hover:underline px-2 py-1"
                                    >
                                        {t('common.select_all', 'Barchasini tanlash')}
                                    </button>
                                    <span className="text-muted">|</span>
                                    <button
                                        type="button"
                                        onClick={() => setForm(p => ({ ...p, organization_ids: [] }))}
                                        className="text-[11px] font-semibold text-muted hover:text-error px-2 py-1"
                                    >
                                        {t('common.deselect_all', 'Tozalash')}
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                {organizations.map((org) => {
                                    const isChecked = (form.organization_ids || []).includes(org.id);
                                    return (
                                        <div
                                            key={org.id}
                                            onClick={() => toggleOrganization(org.id)}
                                            className={`flex items-center gap-3 p-3.5 rounded-xl border transition-all cursor-pointer ${
                                                isChecked
                                                    ? 'bg-brand/10 border-brand/40 text-brand ring-1 ring-brand/30'
                                                    : 'bg-surface-2/50 border-border text-secondary hover:bg-surface-2 hover:text-primary'
                                            }`}
                                        >
                                            <span className={`flex-shrink-0 transition-colors ${isChecked ? 'text-brand' : 'text-muted'}`}>
                                                {isChecked ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                                            </span>
                                            <div className="min-w-0">
                                                <div className="text-sm font-bold truncate">
                                                    {org[`name_${lang}`] || org.name_uz || org.name_ru || org.name_en || org.code}
                                                </div>
                                                {org.code && (
                                                    <span className="text-[10px] font-mono text-muted uppercase">
                                                        {org.code}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Subcategory assignment — grouped by category with Drag & Drop */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                    <label className={labelCls}>{t('users.sections')}</label>
                                    <button
                                        type="button"
                                        onClick={resetToGlobal}
                                        className="flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-surface-2 hover:bg-surface-3 text-muted transition-colors"
                                        title={t('users.reset_order')}
                                    >
                                        <RotateCcw className="w-3 h-3" />
                                        Reset
                                    </button>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-bold
                                    ${form.subcategory_ids.length > 0 ? 'bg-brand/10 text-brand' : 'bg-surface-2 text-muted'}`}>
                                    {form.subcategory_ids.length} / {subcats.length} {t('users.selected_count')}
                                </span>
                            </div>

                            <div className="space-y-4">
                                {subcats.length === 0 ? (
                                    <p className="text-sm text-muted text-center py-6 bg-surface-2/20 border border-border rounded-2xl">{t('users.no_subcats')}</p>
                                ) : (
                                    <DragDropContext onDragEnd={onDragEnd}>
                                        <Droppable droppableId="categories" type="CATEGORY">
                                            {(provided) => (
                                                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                                                    {groupedSubcats.map((group, gi) => {
                                                        const ids = group.items.map(s => s.id);
                                                        const selectedInGroup = ids.filter(id => form.subcategory_ids.includes(id)).length;
                                                        const allSelected = selectedInGroup === ids.length;
                                                        const someSelected = selectedInGroup > 0 && !allSelected;

                                                        return (
                                                            <Draggable key={group.id} draggableId={group.id} index={gi}>
                                                                {(provided, snapshot) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        className={`border border-border rounded-2xl overflow-hidden transition-all ${snapshot.isDragging ? 'z-50 shadow-2xl ring-2 ring-brand/30 bg-surface' : 'bg-surface shadow-sm'}`}
                                                                    >
                                                                        {/* Category header card-like */}
                                                                        <div className="w-full flex items-center gap-3 px-4 py-3 bg-surface-2 border-b border-border">
                                                                            <div {...provided.dragHandleProps} className="text-muted cursor-grab active:cursor-grabbing p-1.5 hover:text-primary hover:bg-surface-3 rounded transition-all">
                                                                                <GripVertical className="w-4 h-4" />
                                                                            </div>

                                                                            <button
                                                                                type="button"
                                                                                onClick={() => toggleCategory(group.items)}
                                                                                className="flex flex-1 items-center gap-3 text-left"
                                                                            >
                                                                                <span className={`flex-shrink-0 transition-colors ${allSelected ? 'text-brand' : someSelected ? 'text-brand' : 'text-muted'}`}>
                                                                                    {allSelected ? <CheckSquare className="w-4 h-4" /> : someSelected ? <Minus className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                                                                </span>
                                                                                <span className="text-xs font-bold uppercase tracking-wider text-secondary flex-1">
                                                                                    {group.name}
                                                                                </span>
                                                                                <span className="text-[10px] text-muted">
                                                                                    {selectedInGroup}/{ids.length}
                                                                                </span>
                                                                            </button>
                                                                        </div>

                                                                        {/* Subcategory list inside category */}
                                                                        <Droppable droppableId={group.id} type="SUBCATEGORY">
                                                                            {(provided) => (
                                                                                <div {...provided.droppableProps} ref={provided.innerRef} className="divide-y divide-border/50 bg-surface/30 min-h-[10px]">
                                                                                    {group.items.map((s, si) => {
                                                                                        const checked = form.subcategory_ids.includes(s.id);
                                                                                        return (
                                                                                            <Draggable key={s.id} draggableId={s.id} index={si}>
                                                                                                {(provided, snapshot) => (
                                                                                                    <div
                                                                                                        ref={provided.innerRef}
                                                                                                        {...provided.draggableProps}
                                                                                                        className={`flex items-center gap-3 px-6 py-3 transition-colors ${checked ? 'bg-brand/5' : ''} ${snapshot.isDragging ? 'bg-brand/10 shadow-lg ring-1 ring-brand/30 border-y border-brand/20' : ''}`}
                                                                                                    >
                                                                                                        <div {...provided.dragHandleProps} className="text-muted/40 cursor-grab active:cursor-grabbing p-1.5 hover:text-primary hover:bg-surface-2 rounded transition-all">
                                                                                                            <GripVertical className="w-3.5 h-3.5" />
                                                                                                        </div>
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            onClick={() => toggleSubcat(s.id)}
                                                                                                            className="flex flex-1 items-center gap-3 text-left"
                                                                                                        >
                                                                                                            <span className={`flex-shrink-0 transition-colors ${checked ? 'text-brand' : 'text-muted'}`}>
                                                                                                                {checked ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                                                                                                            </span>
                                                                                                            <span className={`text-sm font-medium flex-1 ${checked ? 'text-primary' : 'text-secondary'}`}>
                                                                                                                {s.name_ru}
                                                                                                            </span>
                                                                                                            {s.name_uz && <span className="text-xs text-muted ml-2">{s.name_uz}</span>}
                                                                                                        </button>
                                                                                                    </div>
                                                                                                )}
                                                                                            </Draggable>
                                                                                        );
                                                                                    })}
                                                                                    {provided.placeholder}
                                                                                </div>
                                                                            )}
                                                                        </Droppable>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                )}
                            </div>
                        </div>

                        {/* ── Gantt Projects ── */}
                        <div className="bg-surface border border-border p-5 rounded-2xl md:col-span-2">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <label className={labelCls}>Gantt Charts</label>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-bold
                                    ${form.gantt_project_ids.length > 0 ? 'bg-brand/10 text-brand' : 'bg-surface-2 text-muted'}`}>
                                    {form.gantt_project_ids.length} / {ganttProjects.length} {t('users.selected_count')}
                                </span>
                            </div>

                            <div className="space-y-4">
                                {ganttProjects.length === 0 ? (
                                    <p className="text-sm text-muted text-center py-6 bg-surface-2/20 border border-border rounded-2xl">{t('users.no_project', 'Loyiha topilmadi')}</p>
                                ) : (
                                    <div className="border border-border rounded-2xl overflow-hidden bg-surface shadow-sm max-h-[300px] overflow-y-auto">
                                        <div className="divide-y divide-border/50 bg-surface/30">
                                            {ganttProjects.map((p) => {
                                                const checked = form.gantt_project_ids.includes(p.id);
                                                return (
                                                    <div key={p.id} className={`flex items-center gap-3 px-6 py-3 transition-colors ${checked ? 'bg-brand/5' : ''}`}>
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleGanttProject(p.id)}
                                                            className="flex flex-1 items-center gap-3 text-left"
                                                        >
                                                            <span className={`flex-shrink-0 transition-colors ${checked ? 'text-brand' : 'text-muted'}`}>
                                                                {checked ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                                            </span>
                                                            <span className={`text-sm font-medium flex-1 ${checked ? 'text-primary' : 'text-secondary'}`}>
                                                                {p.name}
                                                            </span>
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ── Documents ── */}
                        <div className="bg-surface border border-border p-5 rounded-2xl md:col-span-2">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                     <label className={labelCls}>{t('users.doc_permissions_label', 'Hujjatlar ruxsati')}</label>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-bold
                                    ${form.document_ids.length > 0 ? 'bg-brand/10 text-brand' : 'bg-surface-2 text-muted'}`}>
                                    {form.document_ids.length} / {documents.length} {t('users.selected_count')}
                                </span>
                            </div>

                            <div className="space-y-4">
                                {documents.length === 0 ? (
                                    <p className="text-sm text-muted text-center py-6 bg-surface-2/20 border border-border rounded-2xl">{t('users.docs_not_found', 'Hujjatlar topilmadi')}</p>
                                ) : (
                                    <div className="border border-border rounded-2xl overflow-hidden bg-surface shadow-sm max-h-[300px] overflow-y-auto">
                                        <div className="divide-y divide-border/50 bg-surface/30">
                                            {documents.map((d) => {
                                                const checked = form.document_ids.includes(d.id);
                                                return (
                                                    <div key={d.id} className={`flex items-center gap-3 px-6 py-3 transition-colors ${checked ? 'bg-brand/5' : ''}`}>
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleDocument(d.id)}
                                                            className="flex flex-1 items-center gap-3 text-left"
                                                        >
                                                            <span className={`flex-shrink-0 transition-colors ${checked ? 'text-brand' : 'text-muted'}`}>
                                                                {checked ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                                            </span>
                                                            <span className={`text-sm font-medium flex-1 ${checked ? 'text-primary' : 'text-secondary'}`}>
                                                                #{d.document_number} {d.title}
                                                            </span>
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {formError && (
                            <p className="text-sm text-error font-medium bg-error/5 border border-error/20 rounded-lg px-4 py-2">
                                {formError}
                            </p>
                        )}

                        <div className="flex justify-end gap-3 pt-2">
                            <button type="button" onClick={() => { setShowForm(false); setSearchParams({}); }}
                                className="px-6 py-2.5 rounded-xl border border-border text-secondary hover:bg-surface-2 text-sm font-semibold transition-colors">
                                {t('users.cancel')}
                            </button>
                            <button type="submit" disabled={saving}
                                className="w-40 flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-brand text-inverse text-sm font-bold hover:bg-brand-hover disabled:opacity-60 transition-colors">
                                {saving ? <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" /> : t('users.save')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* ── List View ── */}
            {viewMode === 'list' && (
            <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-sm">
                <table className="w-full text-left text-sm">
                    <thead className="bg-surface-2 border-b border-border text-muted text-xs uppercase">
                        <tr>
                            <th className="px-6 py-3 font-semibold">
                                <div className="flex items-center justify-between group/sort">
                                    <span>{t('users.col_fullname')}</span>
                                    <button
                                        onClick={() => setSortMode(s => s === 'alphabetical' ? 'newest' : 'alphabetical')}
                                        className={`p-1.5 rounded-md border transition-colors ${sortMode === 'newest' ? 'bg-brand/10 text-brand border-brand/20' : 'bg-transparent border-transparent text-muted group-hover/sort:border-border group-hover/sort:bg-surface hover:text-primary'}`}
                                        title={sortMode === 'alphabetical' ? 'Sort by newest' : 'Sort alphabetically'}
                                    >
                                        <ArrowUpDown className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </th>
                            <th className="px-6 py-4 font-semibold">{t('users.col_login')}</th>
                            <th className="px-6 py-4 font-semibold">{t('users.col_position')}</th>
                            <th className="px-6 py-4 font-semibold">{t('users.col_role')}</th>
                            <th className="px-6 py-4 font-semibold text-right">{t('users.col_actions')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {loading ? (
                            <tr><td colSpan={5} className="px-6 py-10 text-center text-muted">{t('users.loading')}</td></tr>
                        ) : filteredUsers.length === 0 ? (
                            <tr><td colSpan={5} className="px-6 py-10 text-center text-muted">{t('users.not_found')}</td></tr>
                        ) : filteredUsers.map((u, i) => (
                            <tr key={u.id} className="hover:bg-surface-2/50 transition-colors">
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center overflow-hidden text-white font-bold text-xs flex-shrink-0 ${avatarBg[i % avatarBg.length]}`}>
                                            {u.show_photo && u.photo ? (
                                                <img src={`${import.meta.env.VITE_API_URL || ''}${u.photo}`} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                ln(u, 'last_name')?.charAt(0).toUpperCase()
                                            )}
                                        </div>
                                        <span className="font-medium">{ln(u, 'last_name')} {ln(u, 'first_name')} {ln(u, 'middle_name')}</span>
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-muted font-mono text-xs">{u.login}</td>
                                <td className="px-6 py-4 text-muted">{pos(u) || '—'}</td>
                                <td className="px-6 py-4">
                                    <div className="flex flex-col gap-1.5">
                                        <span className={`inline-block w-fit px-2 py-0.5 rounded-full text-[10px] font-bold uppercase
                    ${u.role === 'admin' ? 'bg-error/10 text-error' : u.role === 'gantt_user' ? 'bg-blue-500/10 text-blue-500' : u.role === 'doc_user' ? 'bg-purple-500/10 text-purple-500' : 'bg-brand/10 text-brand'}`}>
                                            {u.role.replace('_', ' ')}
                                        </span>
                                        {u.organizations && u.organizations.length > 0 && (
                                            <div className="flex flex-wrap gap-1 max-w-[220px]">
                                                {u.organizations.map((org) => (
                                                    <span key={org.id} className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-surface-2 text-muted border border-border">
                                                        {org[`name_${lang}`] || org.name_uz || org.code}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-right space-x-2">
                                    <button onClick={() => navigate(`/admin/users/${u.id}/history`)}
                                        title={t('users.history_title')}
                                        className="p-2 text-muted hover:text-brand bg-surface-2 rounded-lg border border-border transition-colors">
                                        <History className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => openEdit(u)}
                                        title={t('users.edit_btn')}
                                        disabled={(u.is_superadmin && currentUser?.id !== u.id) || (!currentUser?.is_superadmin && !currentUser?.can_create_users && currentUser?.id !== u.id)}
                                        className={`p-2 rounded-lg border border-border transition-colors ${(u.is_superadmin && currentUser?.id !== u.id) || (!currentUser?.is_superadmin && !currentUser?.can_create_users && currentUser?.id !== u.id) ? 'opacity-50 cursor-not-allowed text-muted bg-surface/50' : 'text-muted hover:text-brand bg-surface-2'}`}>
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleDelete(u)}
                                        title={t('users.delete_btn')}
                                        disabled={u.is_superadmin || (!currentUser?.is_superadmin && !currentUser?.can_delete_users)}
                                        className={`p-2 rounded-lg border border-border transition-colors ${u.is_superadmin || (!currentUser?.is_superadmin && !currentUser?.can_delete_users) ? 'opacity-50 cursor-not-allowed text-muted bg-surface/50' : 'text-muted hover:text-error bg-surface-2'}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            )}

            {/* ── Card View ── */}
            {viewMode === 'card' && (
                loading ? (
                    <div className="text-center py-10 text-muted">{t('users.loading')}</div>
                ) : filteredUsers.length === 0 ? (
                    <div className="text-center py-10 text-muted">{t('users.not_found')}</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {filteredUsers.map((u, i) => {
                            const VITE_API = import.meta.env.VITE_API_URL || '';
                            const canEdit = !(u.is_superadmin && currentUser?.id !== u.id) && (currentUser?.is_superadmin || currentUser?.can_create_users || currentUser?.id === u.id);
                            const canDelete = !u.is_superadmin && (currentUser?.is_superadmin || currentUser?.can_delete_users);
                            return (
                                <div key={u.id} className="bg-surface border border-border rounded-2xl overflow-hidden shadow-sm hover:shadow-md hover:border-brand/30 transition-all flex h-[180px]">
                                    {/* Left — Photo / Avatar */}
                                    <div className="w-[140px] flex-shrink-0 bg-surface-2 flex items-center justify-center overflow-hidden">
                                        {u.show_photo && u.photo ? (
                                            <img
                                                src={`${VITE_API}${u.photo}`}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className={`w-full h-full flex items-center justify-center text-white font-bold text-4xl ${avatarBg[i % avatarBg.length]}`}>
                                                {ln(u, 'last_name')?.charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                    </div>

                                    {/* Right — Info + Actions */}
                                    <div className="flex-1 flex flex-col min-w-0">
                                        {/* Info */}
                                        <div className="flex-1 px-4 py-3 flex flex-col justify-center min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-[10px] font-mono text-muted">{u.login}</span>
                                                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase
                                                    ${u.is_superadmin ? 'bg-amber-500/15 text-amber-400' : u.role === 'admin' ? 'bg-error/10 text-error' : u.role === 'gantt_user' ? 'bg-blue-500/10 text-blue-500' : u.role === 'doc_user' ? 'bg-purple-500/10 text-purple-500' : 'bg-brand/10 text-brand'}`}>
                                                    {u.is_superadmin ? 'Super' : u.role.replace('_', ' ')}
                                                </span>
                                            </div>
                                            <h4 className="font-bold text-sm text-primary leading-tight truncate">
                                                {ln(u, 'last_name')} {ln(u, 'first_name')}
                                            </h4>
                                            {ln(u, 'middle_name') && <p className="text-xs text-muted truncate">{ln(u, 'middle_name')}</p>}

                                            <div className="mt-2 space-y-1">
                                                {pos(u) && (
                                                    <div className="flex items-center gap-2 text-xs text-muted">
                                                        <Briefcase className="w-3 h-3 flex-shrink-0" />
                                                        <span className="truncate">{pos(u)}</span>
                                                    </div>
                                                )}
                                                {u.phone && (
                                                    <div className="flex items-center gap-2 text-xs text-muted">
                                                        <Phone className="w-3 h-3 flex-shrink-0" />
                                                        <span>{u.phone}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="px-4 py-2.5 border-t border-border flex items-center justify-end gap-2 bg-surface-2/30">
                                            <button onClick={() => navigate(`/admin/users/${u.id}/history`)}
                                                title={t('users.history_title')}
                                                className="p-1.5 text-muted hover:text-brand bg-surface-2 rounded-lg border border-border transition-colors">
                                                <History className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => openEdit(u)}
                                                title={t('users.edit_btn')}
                                                disabled={!canEdit}
                                                className={`p-1.5 rounded-lg border border-border transition-colors ${!canEdit ? 'opacity-50 cursor-not-allowed text-muted bg-surface/50' : 'text-muted hover:text-brand bg-surface-2'}`}>
                                                <Edit2 className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => handleDelete(u)}
                                                title={t('users.delete_btn')}
                                                disabled={!canDelete}
                                                className={`p-1.5 rounded-lg border border-border transition-colors ${!canDelete ? 'opacity-50 cursor-not-allowed text-muted bg-surface/50' : 'text-muted hover:text-error bg-surface-2'}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            )}

            {/* ── Bloklangan userlar (faqat superadmin ko'radi) ── */}
            {currentUser?.is_superadmin && users.filter(u => u.is_active === false).length > 0 && (
                <div className="mt-10">
                    {/* Section header */}
                    <div className="flex items-center gap-4 mb-5 p-4 rounded-2xl bg-error/5 border border-error/20">
                        <div className="w-10 h-10 rounded-xl bg-error/10 flex items-center justify-center flex-shrink-0">
                            <ShieldAlert className="w-5 h-5 text-error" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-bold text-sm text-error">{t('users.blocked_section_title', 'Bloklangan foydalanuvchilar')}</h3>
                            <p className="text-xs text-muted mt-0.5">
                                {t('users.blocked_section_desc', '{{count}} ta foydalanuvchi bloklangan — ular tizimga kira olmaydi, lekin ma\'lumotlari saqlanib qolgan. Faqat siz (superadmin) ko\'ra olasiz.', { count: users.filter(u => u.is_active === false).length })}
                            </p>
                        </div>
                        <span className="text-2xl font-black text-error/30">
                            {users.filter(u => u.is_active === false).length}
                        </span>
                    </div>

                    {/* Blocked user cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {users.filter(u => u.is_active === false).map((u, i) => (
                            <div key={u.id} className="relative bg-surface border-2 border-error/40 rounded-2xl overflow-hidden flex h-[160px] grayscale hover:grayscale-0 transition-all duration-300 group">
                                {/* Diagonal stamp */}
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                                    <span className="text-[11px] font-black text-error/40 uppercase tracking-[0.3em] rotate-[-30deg] select-none group-hover:text-error/60 transition-colors">
                                        {t('users.blocked_badge', 'BLOKLANGAN')}
                                    </span>
                                </div>

                                {/* Left avatar */}
                                <div className="w-[130px] flex-shrink-0 bg-surface-2/50 flex items-center justify-center overflow-hidden relative">
                                    {u.show_photo && u.photo ? (
                                        <img
                                            src={`${import.meta.env.VITE_API_URL || ''}${u.photo}`}
                                            alt=""
                                            className="w-full h-full object-cover opacity-40"
                                        />
                                    ) : (
                                        <div className={`w-full h-full flex items-center justify-center text-white font-bold text-4xl opacity-30 ${avatarBg[i % avatarBg.length]}`}>
                                            {ln(u, 'last_name')?.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-error/10" />
                                </div>

                                {/* Right content */}
                                <div className="flex-1 flex flex-col min-w-0 relative z-20">
                                    <div className="flex-1 px-4 py-3 flex flex-col justify-center min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-[10px] font-mono text-muted line-through">{u.login}</span>
                                            <span className="px-1.5 py-0.5 rounded-full text-[8px] font-black bg-error text-white uppercase">
                                                {t('users.blocked_badge', 'Bloklangan')}
                                            </span>
                                        </div>
                                        <h4 className="font-bold text-sm text-secondary leading-tight truncate">
                                            {ln(u, 'last_name')} {ln(u, 'first_name')}
                                        </h4>
                                        {ln(u, 'middle_name') && <p className="text-xs text-muted truncate">{ln(u, 'middle_name')}</p>}
                                        {pos(u) && <p className="text-xs text-muted/60 truncate mt-1">{pos(u)}</p>}
                                    </div>

                                    {/* Restore button */}
                                    <div className="px-4 py-2.5 border-t border-error/20 bg-error/5 flex items-center justify-between gap-2">
                                        <span className="text-[10px] text-muted">{t('users.restore_for', 'Qayta tiklash uchun:')}</span>
                                        <button
                                            onClick={() => handleRestore(u)}
                                            disabled={restoringUser?.id === u.id && isRestoringConfirm}
                                            title={t('users.restore_action', 'Tiklash')}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand text-white text-xs font-bold hover:bg-brand/80 transition-all disabled:opacity-50 shadow-sm"
                                        >
                                            {restoringUser?.id === u.id && isRestoringConfirm
                                                ? <span className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                                : <RotateCcw className="w-3.5 h-3.5" />
                                            }
                                            {t('users.restore_action', 'Tiklash')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>

        {/* ── Password Re-confirmation Modal ── */}

        {showConfirmPwd && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                <div className="bg-surface border border-border rounded-2xl p-8 shadow-2xl w-full max-w-sm mx-4 space-y-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center">
                            <Lock className="w-5 h-5 text-brand" />
                        </div>
                        <div>
                            <h3 className="font-bold text-base">{t('users.confirm', 'Tasdiqlash')}</h3>
                            <p className="text-xs text-muted">{t('users.enter_password_save', 'Saqlash uchun parolingizni kiriting')}</p>
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-muted uppercase tracking-wide">{t('users.password', 'Parol')} <span className="text-error">*</span></label>
                        <input
                            type="password"
                            autoFocus
                            value={confirmPwd}
                            onChange={e => setConfirmPwd(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && confirmSave()}
                            className="w-full px-4 py-2.5 rounded-xl bg-surface-2 border border-border text-primary text-sm focus:outline-none focus:border-brand transition-colors"
                            placeholder="••••••••"
                        />
                        {confirmPwdError && (
                            <p className="text-xs text-error font-medium mt-1">{confirmPwdError}</p>
                        )}
                    </div>

                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => setShowConfirmPwd(false)}
                            className="flex-1 px-4 py-2.5 rounded-xl border border-border text-secondary hover:bg-surface-2 text-sm font-semibold transition-colors"
                        >
                            {t('users.cancel', 'Bekor qilish')}
                        </button>
                        <button
                            type="button"
                            onClick={confirmSave}
                            disabled={saving}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand text-inverse text-sm font-bold hover:bg-brand-hover disabled:opacity-60 transition-colors"
                        >
                            {saving
                                ? <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                : <Check className="w-4 h-4" />
                            }
                            {t('users.confirm', 'Tasdiqlash')}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* ── Custom Delete Confirmation Modal ── */}
        {deletingUser && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                <div className="bg-surface border border-border rounded-2xl p-8 shadow-2xl w-full max-w-md mx-4 space-y-5 animate-in fade-in zoom-in-95 duration-200">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0 mt-1">
                            <ShieldAlert className="w-6 h-6 text-error" />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg text-primary">{t('users.delete_modal_title')}</h3>
                            <p className="text-sm text-muted mt-2 leading-relaxed">
                                <strong className="text-primary">{ln(deletingUser, 'last_name')} {ln(deletingUser, 'first_name')}</strong> {t('users.delete_modal_desc')}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-4 pt-2">
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-muted uppercase tracking-wide">{t('users.delete_modal_login')} <span className="text-error">*</span></label>
                            <input
                                type="text"
                                autoFocus
                                value={deleteCreds.login}
                                onChange={e => setDeleteCreds(p => ({ ...p, login: e.target.value }))}
                                className="w-full px-4 py-3 rounded-xl bg-surface-2 border border-border text-primary text-sm focus:outline-none focus:border-error focus:ring-1 focus:ring-error transition-all"
                                placeholder={t('users.delete_modal_login_placeholder')}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-muted uppercase tracking-wide">{t('users.delete_modal_password')} <span className="text-error">*</span></label>
                            <input
                                type="password"
                                value={deleteCreds.password}
                                onChange={e => setDeleteCreds(p => ({ ...p, password: e.target.value }))}
                                onKeyDown={e => e.key === 'Enter' && confirmDeleteUser()}
                                className="w-full px-4 py-3 rounded-xl bg-surface-2 border border-border text-primary text-sm focus:outline-none focus:border-error focus:ring-1 focus:ring-error transition-all"
                                placeholder={t('users.delete_modal_password_placeholder')}
                            />
                        </div>
                        {deleteError && (
                            <div className="bg-error/10 border border-error/20 rounded-xl p-3 flex items-start gap-2">
                                <ShieldAlert className="w-4 h-4 text-error mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-error font-medium leading-tight">{deleteError}</p>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-3 pt-4 border-t border-border mt-6">
                        <button
                            type="button"
                            onClick={() => setDeletingUser(null)}
                            className="flex-1 px-4 py-3 rounded-xl border border-border text-secondary hover:bg-surface-2 text-sm font-bold transition-all shadow-sm hover:shadow"
                        >
                            {t('users.delete_modal_cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={confirmDeleteUser}
                            disabled={isDeleting}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-error text-white text-sm font-bold hover:bg-error/90 disabled:opacity-60 transition-all shadow-sm hover:shadow shadow-error/20"
                        >
                            {isDeleting
                                ? <span className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                : <Trash2 className="w-5 h-5" />
                            }
                            {t('users.delete_modal_confirm')}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* ── Restore Confirmation Modal ── */}
        {restoringUser && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                <div className="bg-surface border border-border rounded-2xl p-8 shadow-2xl w-full max-w-md mx-4 space-y-5 animate-in fade-in zoom-in-95 duration-200">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0 mt-1">
                            <RotateCcw className="w-6 h-6 text-brand" />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg text-primary">{t('users.restore_modal_title', 'Foydalanuvchini tiklash')}</h3>
                            <p className="text-sm text-muted mt-2 leading-relaxed">
                                <strong className="text-primary">{ln(restoringUser, 'last_name')} {ln(restoringUser, 'first_name')}</strong>{' '}
                                {t('users.restore_modal_desc', 'tiklanadi. Foydalanuvchi tizimga qayta kira oladi. Tasdiqlash uchun loginigiz va parolingizni kiriting.')}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-4 pt-2">
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-muted uppercase tracking-wide">{t('users.restore_modal_login', 'Sizning loginingiz')} <span className="text-error">*</span></label>
                            <input
                                type="text"
                                autoFocus
                                value={restoreCreds.login}
                                onChange={e => setRestoreCreds(p => ({ ...p, login: e.target.value }))}
                                className="w-full px-4 py-3 rounded-xl bg-surface-2 border border-border text-primary text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-all"
                                placeholder={t('users.delete_modal_login_placeholder', 'Masalan: admin')}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-muted uppercase tracking-wide">{t('users.restore_modal_password', 'Sizning parolingiz')} <span className="text-error">*</span></label>
                            <input
                                type="password"
                                value={restoreCreds.password}
                                onChange={e => setRestoreCreds(p => ({ ...p, password: e.target.value }))}
                                onKeyDown={e => e.key === 'Enter' && confirmRestoreUser()}
                                className="w-full px-4 py-3 rounded-xl bg-surface-2 border border-border text-primary text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-all"
                                placeholder="••••••••"
                            />
                        </div>
                        {restoreError && (
                            <div className="bg-error/10 border border-error/20 rounded-xl p-3 flex items-start gap-2">
                                <ShieldAlert className="w-4 h-4 text-error mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-error font-medium leading-tight">{restoreError}</p>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-3 pt-4 border-t border-border mt-6">
                        <button
                            type="button"
                            onClick={() => setRestoringUser(null)}
                            className="flex-1 px-4 py-3 rounded-xl border border-border text-secondary hover:bg-surface-2 text-sm font-bold transition-all shadow-sm hover:shadow"
                        >
                            {t('users.restore_modal_cancel', 'Bekor qilish')}
                        </button>
                        <button
                            type="button"
                            onClick={confirmRestoreUser}
                            disabled={isRestoringConfirm}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brand text-white text-sm font-bold hover:bg-brand/90 disabled:opacity-60 transition-all shadow-sm hover:shadow shadow-brand/20"
                        >
                            {isRestoringConfirm
                                ? <span className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                                : <RotateCcw className="w-5 h-5" />
                            }
                            {t('users.restore_modal_confirm', 'Tiklash')}
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}
